import React from "react";
import { useTranslation } from "react-i18next";
import { Timer } from "lucide-react";
import { Field, Input } from "../common";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";
import { useSectionForm } from "./useSectionForm";

/**
 * The D7 business-day thresholds.
 *
 * These were decided as "global and admin-configurable" and the endpoint has
 * existed since Phase 2, but nothing on the frontend ever reached it — so in
 * practice they could only be changed by calling the API by hand. This closes
 * that.
 *
 * Each one drives something the user can actually observe, so the copy names
 * the consequence rather than the field: a number with no stated effect is a
 * number nobody will touch.
 */

const FIELDS = [
  { key: "unassigned_days",         labelKey: "thresholds.unassigned",        hintKey: "thresholds.unassignedHint" },
  { key: "awaiting_response_days",  labelKey: "thresholds.awaitingResponse",  hintKey: "thresholds.awaitingResponseHint" },
  { key: "stale_days",              labelKey: "thresholds.stale",             hintKey: "thresholds.staleHint" },
];

export default function ThresholdSettings() {
  const { t } = useTranslation();
  const section = useSectionForm({
    path: "/settings/thresholds",
    // ThresholdSettings in schemas.py declares all three fields with no
    // defaults, so a partial body is a 422 -- always send the whole record.
    transform: (form) => ({
      unassigned_days: Number(form.unassigned_days) || 0,
      awaiting_response_days: Number(form.awaiting_response_days) || 0,
      stale_days: Number(form.stale_days) || 0,
    }),
  });

  return (
    <SectionShell
      icon={Timer} tone="amber"
      title={t("thresholds.title")} desc={t("thresholds.desc")}
      error={section.error}
      dirty={section.dirty} saving={section.saving} saved={section.saved}
      onSave={section.save} onDiscard={section.discard}>

      {!section.form ? (
        <p className="text-sm text-muted mt-5">{t("common.loading")}</p>
      ) : (
        <FieldGroups>
          <FieldGroup hint={t("thresholds.businessDaysNote")}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {FIELDS.map(({ key, labelKey, hintKey }) => (
                <div key={key}>
                  <Field label={t(labelKey)}>
                    <Input
                      data-testid={`threshold-${key}`}
                      type="number" min="0" max="365" inputMode="numeric"
                      value={section.form[key]}
                      onChange={section.patch(key)}
                      className="tabular-nums" />
                  </Field>
                  <p className="text-xs text-muted mt-1.5">{t(hintKey)}</p>
                </div>
              ))}
            </div>
          </FieldGroup>
        </FieldGroups>
      )}
    </SectionShell>
  );
}
